/**
 * AtlasFieldGuideModule — THE FIELD GUIDE (2026-09-24; research §4.23: "a catch loop — a net for bugs, observation
 * for birds — a field_guide table, a Passport tab"). The catalog is lib/field_guide_catalog.ts (mirrored); the rows
 * are the server's (`field_guide`, reducers/field_guide.ts).
 *
 *   THE BOOKS (menu bar → Guide): Birds · Bugs · Creatures · Fish. Every species a page — unfound ones are a
 *     silhouette and a hint of where to look; found ones their name, their notes, how you know them (observed, caught,
 *     hunted), how often, since when, and for fish the longest you have landed and where and when they bite. A book
 *     completed pays 250 ⓒ once and writes a line in the Memory Book.
 *   IN THE WORLD: a bird, the woods' swarm or a creature you have not recorded, in front of you and near enough,
 *     offers "E — note the Cobalt Jay in your Field Guide" (the bugs you catch in your hand — a net stroke). Hunting a
 *     creature records it too (the server does that on the kill). Fish are caught (AtlasFishingModule).
 * Levers: `__fieldGuide()`.
 */
import type { App, IModule } from 'enginex';
import type { Identity } from 'spacetimedb';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { interactFocus } from '../lib/interact_focus.js';
import { onInteract, interactPrompt, interactKey } from '../lib/interact_input.js';
import { BIRDS, BUGS, CREATURES, FISH, GUIDE_KINDS, guideEntry, type GuideKind, type GuideEntry, type FishSpec } from '../lib/field_guide_catalog.js';
import { el, tabs, head, chip, muted, card, act, debounced, ms } from './town/town_ui.js';

interface GuideRow { key: string; identity: Identity; kind: string; species: string; count: number; how: string; best: number; firstAt: { microsSinceUnixEpoch: bigint }; lastAt: { microsSinceUnixEpoch: bigint } }
interface Sighting { kind: 'bird' | 'bug' | 'creature'; species: string; x: number; y: number; z: number }
interface FaunaLike { sightings?(): Array<{ kind: 'bird' | 'bug'; species: string; x: number; y: number; z: number }> }
interface CreatureRow { species: string; x: number; z: number; state: string; worldId?: bigint }

const OBSERVE_M: Record<string, number> = { bird: 40, bug: 10, creature: 55 };
const CONE_COS = Math.cos((38 * Math.PI) / 180);
const BOOK_LABEL: Record<GuideKind, string> = { bird: 'Birds', bug: 'Bugs', creature: 'Creatures', fish: 'Fish' };
const BOOK_REWARD = 250;
const HOW_LABEL: Record<string, string> = { observed: 'observed', caught: 'caught', hunted: 'hunted' };
/** A bite window ([from, to] fractions of the day) in words. */
function hourText([a, b]: [number, number]): string {
  if (a === 0 && b === 1) return 'any hour';
  if (a === 0.25 && b === 0.75) return 'by day';
  if (a === 0.78 && b === 0.22) return 'at night';
  if (a === 0.7 && b === 0.82) return 'at dusk';
  if (a === 0.18 && b === 0.3) return 'at dawn';
  const hh = (f: number): string => `${String(Math.floor(f * 24)).padStart(2, '0')}:${String(Math.round((f * 24 % 1) * 60)).padStart(2, '0')}`;
  return `${hh(a)}–${hh(b)}`;
}

function book(kind: GuideKind): GuideEntry[] {
  return kind === 'bird' ? BIRDS : kind === 'bug' ? BUGS : kind === 'creature' ? CREATURES : FISH;
}

export class AtlasFieldGuideModule implements IModule {
  readonly name = 'AtlasFieldGuide';
  private app!: App;
  private win: UiWindow | null = null;
  private tab: GuideKind = 'bird';
  private subbed = false;
  private prompt: HTMLDivElement | null = null;
  private focused: Sighting | null = null;
  private scanAt = 0;
  private candidate: Sighting | null = null;
  private noted = new Set<string>();
  /** F2: the bound Interact key AND the pad's (`atlas:place-requested`), one press — lib/interact_input.ts. */
  private offInteract: (() => void) | null = null;
  private readonly rerender = debounced(() => { if (this.win?.isOpen()) this.render(); }, 250);

  init(app: App): void {
    this.app = app;
    bus.on('atlas:open-field-guide', (p) => this.open(p?.book as GuideKind | undefined));
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    app.world.addSystem(makeSystem('FieldGuide', Phase.UI, 38, () => this.tick()));
    this.offInteract = onInteract(() => this.key());
    const p = document.createElement('div');
    p.id = 'field-guide-prompt';
    p.style.cssText = 'position:fixed;left:50%;bottom:262px;transform:translateX(-50%);z-index:41;display:none;'
      + 'padding:6px 14px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 86%, transparent);border:1px solid color-mix(in srgb, var(--ax-good) 55%, transparent);'
      + 'font:600 13px var(--ax-font);color:var(--ax-text);pointer-events:none';
    document.body.appendChild(p);
    this.prompt = p;
    (window as unknown as { __fieldGuide?: unknown }).__fieldGuide = Object.assign(() => ({
      found: Object.fromEntries(GUIDE_KINDS.map((k) => [k, this.mine(k).length])),
      totals: Object.fromEntries(GUIDE_KINDS.map((k) => [k, book(k).length])),
      candidate: this.candidate, open: !!this.win?.isOpen(), tab: this.tab,
    }), {
      open: (k?: GuideKind) => this.open(k),
      note: (kind: 'bird' | 'bug' | 'creature', species: string) => this.note({ kind, species, x: 0, y: 0, z: 0 }),
    });
  }

  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subbed) return;
    this.subbed = true;
    try {
      const t = (conn.db as unknown as { fieldGuide?: { onInsert(cb: (c: unknown, r: GuideRow) => void): void; onUpdate(cb: () => void): void } }).fieldGuide;
      t?.onInsert((_c, r) => { this.rerender(); if (atlas.identityHex && r.identity.toHexString() === atlas.identityHex) this.newEntry(r); });
      t?.onUpdate(() => this.rerender());
      const me = atlas.identityHex;
      conn.subscriptionBuilder().subscribe([me ? `SELECT * FROM field_guide WHERE identity = 0x${me.replace(/^0x/, '')}` : 'SELECT * FROM field_guide']);
    } catch { /* pre-publish */ }
  }

  private mine(kind?: GuideKind): GuideRow[] {
    const me = atlas.identityHex;
    try {
      const out: GuideRow[] = [];
      for (const r of (atlas.conn?.db as unknown as { fieldGuide?: { iter(): Iterable<GuideRow> } })?.fieldGuide?.iter() ?? []) {
        if (r.identity.toHexString() !== me) continue;
        if (kind && r.kind !== kind) continue;
        out.push(r);
      }
      return out;
    } catch { return []; }
  }
  private has(kind: string, species: string): boolean {
    return this.noted.has(`${kind}:${species}`) || this.mine(kind as GuideKind).some((r) => r.species === species);
  }

  private newEntry(r: GuideRow): void {
    const e = guideEntry(r.kind as GuideKind, r.species);
    const found = this.mine(r.kind as GuideKind).length;
    const total = book(r.kind as GuideKind).length;
    bus.emit('atlas:toast', { text: `New in your Field Guide: ${e?.name ?? r.species} (${found}/${total} ${BOOK_LABEL[r.kind as GuideKind]?.toLowerCase() ?? r.kind})${found === total ? ` — the book is complete: ${BOOK_REWARD} ⓒ` : ''}`, kind: 'success', ms: 4500 });
  }

  // ── observing ──────────────────────────────────────────────────────────────
  private tick(): void {
    const inWorld = atlas.currentWorldId !== null && !atlas.spaceFlight && !atlas.flyMode && !atlas.editMode;
    if (!inWorld) { this.showPrompt(null); this.candidate = null; return; }
    const now = performance.now();
    if (now - this.scanAt > 400) { this.scanAt = now; this.candidate = this.scan(); }
    const c = this.candidate;
    const focus = interactFocus();
    if (c) {
      const d = Math.hypot(c.x - atlas.localX, c.z - atlas.localZ);
      const label = this.label(c);
      focus.claim({ id: `guide:${c.kind}:${c.species}`, dist: Math.max(2, d * 0.5), priority: 0, label });
    }
    const owned = c && focus.owns(`guide:${c.kind}:${c.species}`) ? c : null;
    this.focused = owned;
    this.showPrompt(owned ? this.label(owned) : null);
  }

  private label(c: Sighting): string {
    const name = guideEntry(c.kind, c.species)?.name ?? c.species;
    return interactPrompt(c.kind === 'bug' ? `catch a ${name} for your Field Guide` : `note the ${name} in your Field Guide`);
  }

  /** The nearest unrecorded species in front of the viewer, within its book's reach. */
  private scan(): Sighting | null {
    const list: Sighting[] = [];
    const fauna = this.app.getModule('Fauna') as unknown as FaunaLike | undefined;
    for (const s of fauna?.sightings?.() ?? []) list.push(s);
    try {
      for (const c of (atlas.conn?.db as unknown as { creature?: { iter(): Iterable<CreatureRow> } })?.creature?.iter() ?? []) {
        if (c.state === 'dead') continue;
        if (Math.abs(c.x - atlas.localX) > 80 || Math.abs(c.z - atlas.localZ) > 80) continue;
        list.push({ kind: 'creature', species: c.species, x: c.x, y: atlas.localY, z: c.z });
      }
    } catch { /* not subscribed */ }
    const fx = -Math.sin(atlas.localYaw), fz = -Math.cos(atlas.localYaw);
    let best: Sighting | null = null, bd = Infinity;
    for (const s of list) {
      if (!guideEntry(s.kind, s.species) || this.has(s.kind, s.species)) continue;
      const dx = s.x - atlas.localX, dz = s.z - atlas.localZ;
      const d = Math.hypot(dx, dz);
      if (d > OBSERVE_M[s.kind]) continue;
      if (s.kind !== 'bug' && d > 3 && (dx * fx + dz * fz) / d < CONE_COS) continue; // in front of you (a swarm is all around)
      if (d < bd) { bd = d; best = s; }
    }
    return best;
  }

  private key(): void {
    const f = this.focused;
    if (!f || !interactFocus().owns(`guide:${f.kind}:${f.species}`)) return;
    void this.note(f);
  }

  private async note(s: Sighting): Promise<boolean> {
    const conn = atlas.conn;
    if (!conn) return false;
    const ok = await act(conn.reducers.recordSighting({ kind: s.kind, species: s.species }), '');
    if (ok) { this.noted.add(`${s.kind}:${s.species}`); this.candidate = null; }
    return ok;
  }

  private showPrompt(text: string | null): void {
    const p = this.prompt;
    if (!p) return;
    const show = text !== null;
    if ((p.style.display !== 'none') !== show) p.style.display = show ? 'block' : 'none';
    if (show && p.textContent !== text) p.textContent = text;
  }

  // ── the books ──────────────────────────────────────────────────────────────
  private open(k?: GuideKind): void {
    if (k && (GUIDE_KINDS as string[]).includes(k)) this.tab = k;
    if (!this.win) {
      this.win = makeWindow({ id: 'field-guide', title: 'Field Guide', icon: 'compass', width: 560, height: 620, minWidth: 420, minHeight: 360 });
      this.win.body.style.cssText += 'display:flex;flex-direction:column;gap:10px;padding:10px 12px;overflow:auto;font:13px var(--ax-font);color:var(--ax-text)';
    }
    this.win.setOpen(true);
    this.render();
  }

  private render(): void {
    const w = this.win;
    if (!w) return;
    w.body.replaceChildren();
    w.body.appendChild(tabs(GUIDE_KINDS.map((k) => ({ key: k, label: `${BOOK_LABEL[k]} ${this.mine(k).length}/${book(k).length}` })), this.tab, (k) => { this.tab = k as GuideKind; this.render(); }));
    const found = this.mine(this.tab);
    const total = book(this.tab).length;
    w.body.appendChild(head(`${BOOK_LABEL[this.tab]} — ${found.length} of ${total}`));
    w.body.appendChild(muted(this.tab === 'fish'
      ? 'Fish are caught: face the sea, a lake or a river and press ' + interactKey() + ' to cast; hold ' + interactKey() + ' to reel when it bites. What bites depends on the water, the hour, the weather and the land around it.'
      : this.tab === 'bug' ? 'The woods\' swarms change with the hour — stand among them and catch one (' + interactKey() + ').'
      : this.tab === 'creature' ? 'Watch a creature from a safe distance (' + interactKey() + '), or bring one down — the hunt is noted too.'
      : 'Look at a bird and note it (E). Some keep to the woods, some to the coasts and lakes, some to the towns — and some only to the sky.'));
    w.body.appendChild(muted(found.length >= total ? 'This book is complete.' : `Complete the book for ${BOOK_REWARD} ⓒ and a line in the Memory Book.`));
    const byKey = new Map(found.map((r) => [r.species, r]));
    for (const e of book(this.tab)) w.body.appendChild(this.page(e, byKey.get(e.key) ?? null));
  }

  private page(e: GuideEntry, r: GuideRow | null): HTMLDivElement {
    const c = card();
    const top = el('div', 'display:flex;align-items:center;gap:8px;flex-wrap:wrap');
    top.appendChild(el('div', `font-weight:600;${r ? '' : 'color:var(--ax-muted)'}`, r ? e.name : '? ? ?'));
    const fish = this.tab === 'fish' ? (e as FishSpec) : null;
    if (fish) top.appendChild(chip(fish.rarity, fish.rarity === 'epic' ? 'warn' : fish.rarity === 'rare' ? 'info' : ''));
    if (r) {
      top.appendChild(chip(`${HOW_LABEL[r.how] ?? r.how} × ${r.count}`, 'good'));
      if (fish && r.best > 0) top.appendChild(chip(`best ${Math.round(r.best)} cm`, 'info'));
    }
    c.appendChild(top);
    c.appendChild(muted(r ? e.blurb : 'Not yet found.'));
    if (fish) {
      const hours = fish.hours.map(hourText).join(', ');
      const bits = [`in the ${fish.waters.join(' or the ')}`, hours];
      if (fish.weather.length) bits.push(`in ${fish.weather.join(' or ')}`);
      if (fish.biomes.length) bits.push(`where the land is ${fish.biomes.map((b) => b.replace(/_/g, ' ')).join(', ')}`);
      bits.push(`${fish.cm[0]}–${fish.cm[1]} cm`);
      c.appendChild(muted(`Bites ${bits.join(' · ')}.`));
    }
    if (r) c.appendChild(muted(`First ${new Date(ms(r.firstAt)).toLocaleDateString()} · last ${new Date(ms(r.lastAt)).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`));
    return c;
  }

  dispose(): void {
    this.offInteract?.(); this.offInteract = null;
    this.prompt?.remove();
    this.win?.root.remove();
  }
}

